import React, { createContext, useContext, useState, useEffect } from 'react';
import { logger } from '@/services/LoggingService';
import { VezgoConnection, VezgoAccount, ConnectionProcessingState } from '../types';
import { connectionService } from '../services/ConnectionService';
import { useVezgoNavigation } from './navigation';
import { vezgoConfig } from '@/config';
// CRITICAL FIX: Use a single Supabase client across the entire app
import { supabase } from '@/integrations/supabase/client';
import { tokenService } from '../services/TokenService';
import { ensureValidToken } from '@/services/AuthAwareApiService';

export interface VezgoContextState {
  state: {
    isConnected: boolean;
    isLoading: boolean;
    error: any;
    provider: string;
    connection: VezgoConnection | null;
    accounts: VezgoAccount[];
    processingState: ConnectionProcessingState;
    processingProgress: number;
    isProcessing: boolean;
    providerDisplayName: string;
    callbackResult: any;
    isRefreshInProgress: boolean;
    logoUrl: string | null;
    lastRefreshSuccess: boolean;
  },
  
  actions: {
    connect: (provider?: string) => Promise<void>;
    disconnect: () => Promise<void>;
    refreshConnection: () => Promise<VezgoAccount[]>;
    startProcessing: () => void;
    completeProcessing: () => void;
    resetProcessing: () => void;
    setProcessingState: (state: ConnectionProcessingState) => void;
    setProcessingProgress: (progress: number) => void;
    setCallbackResult: (result: any) => void;
    detectReloadLoop: () => boolean;
    isEmptyButValidConnection: () => boolean;
  }
}

const VezgoContext = createContext<VezgoContextState>({
  state: {
    isConnected: false,
    isLoading: false,
    error: null,
    provider: '',
    connection: null,
    accounts: [],
    processingState: ConnectionProcessingState.IDLE,
    processingProgress: 0,
    isProcessing: false,
    providerDisplayName: 'Provider',
    callbackResult: null,
    isRefreshInProgress: false,
    logoUrl: null,
    lastRefreshSuccess: false,
  },
  
  actions: {
    connect: async () => {},
    disconnect: async () => {},
    refreshConnection: async () => [],
    startProcessing: () => {},
    completeProcessing: () => {},
    resetProcessing: () => {},
    setProcessingState: () => {},
    setProcessingProgress: () => {},
    setCallbackResult: () => {},
    detectReloadLoop: () => false,
    isEmptyButValidConnection: () => false
  }
});

interface VezgoProviderProps {
  children: React.ReactNode;
  initialProvider?: string;
}

export const useVezgoContext = () => useContext(VezgoContext);

export const VezgoProvider: React.FC<VezgoProviderProps> = ({ children, initialProvider = 'vezgodemo' }) => {
  const vezgoNavigation = useVezgoNavigation();
  
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [provider, setProvider] = useState(() => {
    const contextProvider = vezgoNavigation.getProvider();
    return contextProvider || initialProvider;
  });
  const [connection, setConnection] = useState<VezgoConnection | null>(null);
  const [accounts, setAccounts] = useState<VezgoAccount[]>([]);
  const [processingState, setProcessingState] = useState<ConnectionProcessingState>(ConnectionProcessingState.IDLE);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [providerDisplayName, setProviderDisplayName] = useState('Provider');
  const [callbackResult, setCallbackResult] = useState<any>(null);
  const [isRefreshInProgress, setIsRefreshInProgress] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [lastRefreshSuccess, setLastRefreshSuccess] = useState(false);
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  
  useEffect(() => {
    const loadInitialState = async () => {
      setIsLoading(true);
      try {
        await connectionService.initialize();
        
        const existingConnection = connectionService.getConnection(provider);
        
        if (existingConnection) {
          setConnection(existingConnection);
          setIsConnected(existingConnection.status === 'connected');
          setProviderDisplayName(existingConnection.metadata?.providerDisplayName || 'Provider');
          setLogoUrl(existingConnection.metadata?.logoUrl || null);
          
          if (existingConnection.status === 'connected') {
            setProcessingState(ConnectionProcessingState.LOADING_ACCOUNTS);
            try {
              await refreshConnection();
              logger.info('VezgoProvider', 'Successfully loaded initial accounts');
            } catch (err) {
              logger.warn('VezgoProvider', 'Failed to load initial accounts', {
                error: err instanceof Error ? err.message : String(err)
              });
            }
          }
        }
      } catch (err) {
        setError(err);
        logger.error('VezgoProvider', 'Error loading initial state', {
          error: err instanceof Error ? err.message : String(err)
        });
      } finally {
        setIsLoading(false);
        setProcessingState(ConnectionProcessingState.IDLE);
      }
    };
    
    loadInitialState();
  }, [provider]);
  
  useEffect(() => {
    vezgoNavigation.saveProvider(provider);
  }, [provider, vezgoNavigation]);
  
  const startProcessing = () => {
    setIsProcessing(true);
  };
  
  const completeProcessing = () => {
    setIsProcessing(false);
  };
  
  const resetProcessing = () => {
    setIsProcessing(false);
    setProcessingProgress(0);
    setProcessingState(ConnectionProcessingState.IDLE);
  };
  
  const detectReloadLoop = (): boolean => {
    return vezgoNavigation.detectReloadLoop();
  };
  
  const isEmptyButValidConnection = (): boolean => {
    return isConnected && (accounts?.length || 0) === 0 && !isLoading && !error;
  };
  
  const connect = async (targetProvider?: string) => {
    const providerToConnect = targetProvider || provider;
    setIsLoading(true);
    setError(null);
    setProcessingState(ConnectionProcessingState.CONNECTING);
    
    try {
      const state = connectionService.generateState();
      
      vezgoNavigation.saveState(state);
      
      vezgoNavigation.saveCurrentLocation(providerToConnect);
      
      const redirectUri = vezgoConfig.redirectUri;
      const connectUrl = `${vezgoConfig.connectUrl}?client_id=${vezgoConfig.clientId}&scope=transactions,accounts,identity&state=${state}&redirect_uri=${encodeURIComponent(redirectUri)}`;
      
      logger.info('VezgoProvider', 'Redirecting to Vezgo Connect', {
        connectUrl,
        provider: providerToConnect,
        redirectUri
      });
      
      window.location.href = connectUrl;
    } catch (err) {
      setError(err);
      logger.error('VezgoProvider', 'Error during connect', {
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setIsLoading(false);
      setProcessingState(ConnectionProcessingState.IDLE);
    }
  };
  
  const disconnect = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await connectionService.deleteConnection(provider);
      
      if (result) {
        setIsConnected(false);
        setConnection(null);
        setAccounts([]);
        setProviderDisplayName('Provider');
        setLogoUrl(null);
      } else {
        logger.warn('VezgoProvider', 'Disconnect failed, connection not found');
      }
    } catch (err) {
      setError(err);
      logger.error('VezgoProvider', 'Error during disconnect', {
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const refreshConnection = async (): Promise<VezgoAccount[]> => {
    setIsLoading(true);
    setIsRefreshInProgress(true);
    setError(null);
    setProcessingState(ConnectionProcessingState.REFRESHING);
    let newAccounts: VezgoAccount[] = [];
    
    try {
      const vezgoAccountId = connection?.vezgo_account_id;
      
      if (!vezgoAccountId) {
        throw new Error('No Vezgo account ID found');
      }

      const token = await tokenService.getToken('vezgo');
      if (!token) {
        throw new Error('Unable to obtain Vezgo token');
      }

      // Ensure valid token before making API call
      await ensureValidToken('vezgo-get-accounts');

      const { data, error } = await supabase.functions.invoke('vezgo-get-accounts', {
        body: {
          vezgoAccountId,
          token,
          clientId: vezgoConfig.clientId
        }
      });
      
      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch accounts');
      }
      
      newAccounts = data.accounts || [];
      
      setAccounts(newAccounts);
      setIsConnected(true);
      setLastRefreshSuccess(true);
      
      logger.info('VezgoProvider', 'Successfully refreshed connection', {
        accountCount: newAccounts.length,
        vezgoAccountId
      });
    } catch (err) {
      setError(err);
      setLastRefreshSuccess(false);
      logger.error('VezgoProvider', 'Error during refresh connection', {
        error: err instanceof Error ? err.message : String(err)
      });
      throw err;
    } finally {
      setIsLoading(false);
      setIsRefreshInProgress(false);
      setProcessingState(ConnectionProcessingState.COMPLETE);
      setRefreshAttempts(0);
    }
    
    return newAccounts;
  };
  
  const value: VezgoContextState = {
    state: {
      isConnected,
      isLoading,
      error,
      provider,
      connection,
      accounts,
      processingState,
      processingProgress,
      isProcessing,
      providerDisplayName,
      callbackResult,
      isRefreshInProgress,
      logoUrl,
      lastRefreshSuccess,
    },
    
    actions: {
      connect,
      disconnect,
      refreshConnection,
      startProcessing,
      completeProcessing,
      resetProcessing,
      setProcessingState,
      setProcessingProgress,
      setCallbackResult,
      detectReloadLoop,
      isEmptyButValidConnection
    }
  };
  
  return (
    <VezgoContext.Provider value={value}>
      {children}
    </VezgoContext.Provider>
  );
};
